/**
 * CateringCapabilitiesService — cached catering feature flags per location.
 *
 * RESPONSIBILITY: Single-call feature discovery for a location. Caches results
 * in a Map so repeat calls for the same location don't re-fetch. Exposes
 * signals for UI conditional rendering (e.g., hiding catering buttons when
 * the feature is disabled for a location).
 *
 * Does NOT manage vendors, menus, or orders — see CateringApiService for those.
 *
 * SIGNALS:
 *   capabilities (readonly)   — Map<locationId, CateringCapabilities>
 *   loading (readonly)        — true while fetching
 *
 * USED BY: BookMeetingPanel (conditional catering buttons), SpaceDetailPanel,
 *   ScheduleComponent (tab visibility), HomeComponent (widget visibility)
 *
 * BACKEND CONTRACT: cateringCapabilities query in catering.resolver.ts
 *
 * AI CONTEXT: The Map-based cache means a component can call getCapabilities()
 * on every location change without triggering redundant network requests.
 * Use refresh() to force a re-fetch (e.g., after admin changes).
 */
import { Injectable, inject, signal } from '@angular/core';
import { CateringApiService } from './catering-api.service';
import { CateringCapabilities } from '../models/catering.model';
import { ApiError, errorFromException } from '../models/api-result';

@Injectable({ providedIn: 'root' })
export class CateringCapabilitiesService {
  private readonly api = inject(CateringApiService);

  private readonly _capabilities = signal<Map<string, CateringCapabilities>>(new Map());
  private readonly _loading = signal(false);
  private readonly _error = signal<ApiError | null>(null);

  readonly capabilities = this._capabilities.asReadonly();
  readonly loading = this._loading.asReadonly();
  /** Typed error from the last failed capabilities lookup. Useful for the
   *  catering surfaces to render a small inline notice when the feature
   *  cannot be queried (rather than silently rendering as disabled). */
  readonly error = this._error.asReadonly();

  /** Get capabilities for a location (returns cached if available). */
  async getCapabilities(locationId: string): Promise<CateringCapabilities | null> {
    const cached = this._capabilities().get(locationId);
    if (cached) return cached;

    this._loading.set(true);
    this._error.set(null);
    try {
      const caps = await this.api.loadCapabilities(locationId);
      if (caps) {
        this._capabilities.update((map) => {
          const next = new Map(map);
          next.set(locationId, caps);
          return next;
        });
      }
      return caps;
    } catch (cause) {
      const err = errorFromException(cause);
      console.warn('[CateringCapabilities] loadCapabilities error:', err.detail ?? err.message);
      this._error.set(err);
      return null;
    } finally {
      this._loading.set(false);
    }
  }

  /** Check if catering is enabled for a location (uses cache, synchronous). */
  isEnabled(locationId: string): boolean {
    return this._capabilities().get(locationId)?.enabled ?? false;
  }

  /** Force refresh capabilities for a location (evicts cache then re-fetches). */
  async refresh(locationId: string): Promise<void> {
    this._capabilities.update(map => {
      const next = new Map(map);
      next.delete(locationId);
      return next;
    });
    await this.getCapabilities(locationId);
  }
}
